import React, { Component } from 'react';
import { getMusicEnginePodcastDetail, getMusicEngineArtistFollowers, musicEngineToggleFavorite, musicEnginePost, musicEngineGet, getUnifiedCurrentUser } from '../../api/api';
import PageSpinner from 'components/Spinner/PageSpinner';
import Header from 'components/Header/Header';
import Style from './podcastDetail.module.css';
import playbutton from 'assets/play-button.png';
import ReadMoreText from 'components/ReadMoreText/ReadMoreText';
import spinGIF from 'assets/spinner.gif';
import defaultImage from 'assets/logo_short.png';
import { logEventPagesViewed, logEventPodcastFollowed } from 'utils/analytics';

class PodcastDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            podcast: null,
            episodes: [],
            selectedEpisode: null,
            following: false,
            podcastSource: null,
            // MusicEngine episodes pagination
            meCurrentPage: 1,
            meLastPage: 1,
            mePerPage: 20,
            meTotalEpisodes: 0,
            meHasMore: false,
            mePages: {},
            selectedIndex: -1,
            skip: 0,
            nextSkip: 0,
            nextPage: true,
            activeIndex: -3,
            nextLoading: false,
            previousLoading: false,
            loadingText: '',
            loadingAnimation: null,
            podcastLoading: true,
            episodeLoading: false,
            successMessage: '',
            errorMessage: ''
        };
    }

    async componentDidMount() {
        // Track page view (non-blocking)
        logEventPagesViewed(null, window.location.href, 'podcastdetail_page', 'PodcastDetail').catch(() => {});
        
        await this.fetchUser();
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    }

    fetchUser = async () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        try {
            const { user } = await getUnifiedCurrentUser();
            this.setState({ user }, () => {
                this.getPodcasts();
            });
        } catch (error) {
            this.getPodcasts();
        }
    };

    // Compute total episodes quickly from last_page if provided, otherwise fall back to background paging
    computeMeTotalEpisodesFromLastPage = async (lastPage, perPage, podcastId) => {
        try {
            // If last_page is known and total isn't provided, we can estimate as (lastPage - 1) * perPage + size(last page)
            // Fetch only the last page to determine its size
            const resp = await musicEngineGet(`podcast/${podcastId}?page=${lastPage}`);
            const meData = resp?.data || resp;
            const mePodcast = meData?.podcast || meData;
            const ep = mePodcast?.episodes;
            const lastPageItems = (ep && typeof ep === 'object' && Array.isArray(ep.data)) ? ep.data : (Array.isArray(ep) ? ep : []);
            const total = (Math.max(0, (lastPage - 1)) * (perPage || 20)) + lastPageItems.length;
            if (total > 0) {
                this.setState({ meTotalEpisodes: total, podcast: { ...this.state.podcast, episodesCount: total } });
            }
        } catch (e) {
            // Ignore errors; we'll fall back to incremental counting
        }
    };

    // Background count by paging forward until fewer than perPage items are returned
    backgroundCountMeTotalEpisodes = async (podcastId, startPage = 2, perPage = 20) => {
        try {
            let page = startPage;
            let runningTotal = this.state.meTotalEpisodes || (this.state.episodes ? this.state.episodes.length : 0);
            while (true) {
                const resp = await musicEngineGet(`podcast/${podcastId}?page=${page}`);
                const meData = resp?.data || resp;
                const mePodcast = meData?.podcast || meData;
                const ep = mePodcast?.episodes;
                const items = (ep && typeof ep === 'object' && Array.isArray(ep.data)) ? ep.data : (Array.isArray(ep) ? ep : []);
                if (!items || items.length === 0) {
                    break;
                }
                runningTotal += items.length;
                if (items.length < (perPage || 20)) {
                    break;
                }
                page += 1;
            }
            if (runningTotal > 0) {
                this.setState({ meTotalEpisodes: runningTotal, podcast: { ...this.state.podcast, episodesCount: runningTotal } });
            }
        } catch (e) {
            // Silent fail
        }
    };

    getPodcasts = async () => {
        const { id } = this.props.match.params;
        this.setState({ podcastLoading: true, errorMessage: '' });

        // Helper: map MusicEngine podcast payload to local shape
        const mapMEPodcast = (mePodcast) => {
            if (!mePodcast) return null;
            return {
                _id: mePodcast.id || mePodcast._id || mePodcast.podcastId || mePodcast.podcast_id,
                title: mePodcast.title || mePodcast.name || '',
                author: mePodcast.author || mePodcast.creator || (mePodcast.artist ? mePodcast.artist.name : ''),
                description: mePodcast.description || mePodcast.summary || '',
                thumbnailURL80x80: mePodcast.image || mePodcast.artwork || mePodcast.artwork_url || '',
                // Prefer podcast loves as follower count (reflects follow/unfollow)
                followers: (typeof mePodcast.loves === 'number' ? mePodcast.loves : (mePodcast.followers || 0)),
                // MusicEngine returns podcast JSON directly; when api route is used, episodes is a paginator
                episodesCount: (
                    Array.isArray(mePodcast.episodes)
                        ? mePodcast.episodes.length
                        : ((mePodcast.episodes && mePodcast.episodes.total) || mePodcast.episodesCount || 0)
                ),
                episodes: Array.isArray(mePodcast.episodes) ? mePodcast.episodes : (mePodcast.episodes && mePodcast.episodes.data ? mePodcast.episodes.data : []),
                // Persisted follow state from backend: prefer 'favorite' (ME), fallback to legacy 'isFollow'
                isFollow: (typeof mePodcast.favorite === 'boolean') ? mePodcast.favorite : !!mePodcast.isFollow
            };
        };

        try {
            // Try MusicEngine first as requested
            const me = await getMusicEnginePodcastDetail(id);
            const meData = me?.data || me; // support either {data: {...}} or direct
            const rawPodcast = meData?.podcast || meData;
            const mapped = mapMEPodcast(rawPodcast);
            if (mapped) {
                // Work out pagination details before rendering to ensure total is correct on first paint
                const ep = rawPodcast && rawPodcast.episodes;
                let currentPage = 1;
                let lastPage = 1;
                const perPageFixed = 20;
                let pageArr = [];
                let pageSlice = [];
                let total = 0;
                if (ep && typeof ep === 'object' && !Array.isArray(ep)) {
                    currentPage = ep.current_page || ep.currentPage || 1;
                    lastPage = ep.last_page || ep.lastPage || 1;
                    total = ep.total || ep.count || 0;
                    pageArr = Array.isArray(ep.data) ? ep.data : [];
                    pageSlice = pageArr.slice(0, perPageFixed);
                    if (!(typeof total === 'number' && total > 0)) {
                        // If total missing, compute synchronously before rendering
                        if (typeof lastPage === 'number' && lastPage > 1) {
                            try {
                                const lastResp = await musicEngineGet(`podcast/${id}?page=${lastPage}`);
                                const lastData = lastResp?.data || lastResp;
                                const lastPodcast = lastData?.podcast || lastData;
                                const lastEp = lastPodcast?.episodes;
                                const lastItems = (lastEp && typeof lastEp === 'object' && Array.isArray(lastEp.data)) ? lastEp.data : (Array.isArray(lastEp) ? lastEp : []);
                                total = (Math.max(0, lastPage - 1) * perPageFixed) + lastItems.length;
                            } catch (e) { total = pageSlice.length; }
                        } else if (pageSlice.length === perPageFixed) {
                            // Page forward until we find a short page
                            let page = 2;
                            total = pageSlice.length;
                            let keepPaging = true;
                            let guard = 0;
                            while (keepPaging && guard < 100) {
                                try {
                                    const respN = await musicEngineGet(`podcast/${id}?page=${page}`);
                                    const dataN = respN?.data || respN;
                                    const podN = dataN?.podcast || dataN;
                                    const epN = podN?.episodes;
                                    const itemsN = (epN && typeof epN === 'object' && Array.isArray(epN.data)) ? epN.data : (Array.isArray(epN) ? epN : []);
                                    const len = Array.isArray(itemsN) ? itemsN.length : 0;
                                    total += len;
                                    if (len < perPageFixed || len === 0) {
                                        keepPaging = false;
                                        lastPage = page;
                                        break;
                                    }
                                    page += 1;
                                    guard += 1;
                                } catch (e) {
                                    keepPaging = false;
                                }
                            }
                        } else {
                            total = pageSlice.length;
                        }
                    }
                } else if (Array.isArray(mapped.episodes)) {
                    // Episodes array provided without paginator; compute total synchronously by paging forward
                    pageSlice = mapped.episodes.slice(0, perPageFixed);
                    total = mapped.episodes.length;
                    currentPage = 1;
                    lastPage = 1;
                    if (pageSlice.length === perPageFixed) {
                        let page = 2;
                        let keepPaging = true;
                        let guard = 0;
                        while (keepPaging && guard < 100) {
                            try {
                                const respN = await musicEngineGet(`podcast/${id}?page=${page}`);
                                const dataN = respN?.data || respN;
                                const podN = dataN?.podcast || dataN;
                                const epN = podN?.episodes;
                                const itemsN = (epN && typeof epN === 'object' && Array.isArray(epN.data)) ? epN.data : (Array.isArray(epN) ? epN : []);
                                const len = Array.isArray(itemsN) ? itemsN.length : 0;
                                total += len;
                                if (len < perPageFixed || len === 0) {
                                    keepPaging = false;
                                    lastPage = page;
                                    break;
                                }
                                page += 1;
                                guard += 1;
                            } catch (e) {
                                keepPaging = false;
                            }
                        }
                    }
                }

                // Ensure mapped count reflects computed total
                if (total && (!mapped.episodesCount || mapped.episodesCount !== total)) {
                    mapped.episodesCount = total;
                }

                const hasMore = (typeof lastPage === 'number' && currentPage < lastPage) || (pageSlice.length === perPageFixed);

                this.setState({
                    podcast: mapped,
                    following: !!mapped.isFollow,
                    podcastSource: 'musicengine',
                    podcastLoading: false,
                    episodeLoading: false,
                    episodes: pageSlice,
                    meCurrentPage: currentPage,
                    meLastPage: (typeof lastPage === 'number' && lastPage > 0) ? lastPage : 1,
                    mePerPage: perPageFixed,
                    meTotalEpisodes: total || 0,
                    meHasMore: !!hasMore,
                    mePages: { 1: pageSlice }
                }, async () => {
                    // If loves present on podcast, prefer that and skip artist fetch
                    if (typeof rawPodcast?.loves === 'number') {
                        this.setState(prev => ({ podcast: { ...prev.podcast, followers: rawPodcast.loves } }));
                        return;
                    }
                    // Otherwise, optionally fetch artist followers as a fallback
                    const artistId = rawPodcast?.artist_id || rawPodcast?.artistId || (rawPodcast?.artist && (rawPodcast.artist.id || rawPodcast.artist._id));
                    if (artistId) {
                        try {
                            const followersResp = await getMusicEngineArtistFollowers(artistId);
                            const artistObj = followersResp?.artist || followersResp;
                            const followerCount = Array.isArray(artistObj?.followers) ? artistObj.followers.length : (artistObj?.follower_count || 0);
                            this.setState(prev => ({ podcast: { ...prev.podcast, followers: followerCount } }));
                        } catch (e) {
                            // ignore followers error
                        }
                    }
                });
                return;
            }
        } catch (err) {
            this.setState({
                podcastLoading: false,
                errorMessage: 'Error fetching podcast details. Please try again.'
            });
        }
    };

    // Fetch a specific page of episodes from MusicEngine and display exactly that page
    fetchEpisodesFromMusicEngine = async (pageNumber) => {
        try {
            const { id } = this.props.match.params;
            this.setState({ episodeLoading: true });
            const resp = await musicEngineGet(`podcast/${id}?page=${pageNumber}`);
            const meData = resp?.data || resp;
            const mePodcast = meData?.podcast || meData;
            const ep = mePodcast?.episodes;
            let newEpisodes = [];
            let currentPage = pageNumber;
            let lastPage = this.state.meLastPage;
            let perPage = 20;
            let total = this.state.meTotalEpisodes;
            if (ep && typeof ep === 'object' && !Array.isArray(ep)) {
                newEpisodes = Array.isArray(ep.data) ? ep.data : [];
                currentPage = ep.current_page || ep.currentPage || pageNumber;
                lastPage = ep.last_page || ep.lastPage || lastPage;
                perPage = 20;
                total = ep.total || ep.count || total;
            } else if (Array.isArray(ep)) {
                newEpisodes = ep;
                currentPage = pageNumber;
                lastPage = Math.max(lastPage, pageNumber);
                perPage = 20;
                total = Math.max(total, (pageNumber - 1) * perPage + newEpisodes.length);
            }
            // Decide visibility purely based on fixed per-page size
            const pageSlice = newEpisodes.slice(0, perPage || 20);
            this.setState(prev => {
                const mePages = { ...prev.mePages, [pageNumber]: pageSlice };
                const allEpisodes = pageSlice; // show only this page
                const hasMore = (typeof lastPage === 'number' && currentPage < lastPage) || (pageSlice.length === (perPage || 20));
                return {
                    episodes: allEpisodes,
                    mePages,
                    meCurrentPage: currentPage,
                    meLastPage: lastPage,
                    mePerPage: 20,
                    meTotalEpisodes: total || prev.meTotalEpisodes,
                    meHasMore: !!hasMore,
                    nextLoading: false,
                    previousLoading: false,
                    episodeLoading: false,
                    loadingText: ''
                };
            });
        } catch (error) {
            this.setState({
                episodeLoading: false,
                nextLoading: false,
                previousLoading: false,
                loadingText: '',
                errorMessage: error || 'Error fetching episodes'
            });
        } finally {
            if (this.loadingAnimation) {
                clearInterval(this.loadingAnimation);
            }
        }
    };

    getEpisodes = async () => {
        // Legacy method - now using MusicEngine pagination via fetchEpisodesFromMusicEngine
        // This method is kept for backwards compatibility but should use MusicEngine
        if (this.state.podcastSource === 'musicengine') {
            await this.fetchEpisodesFromMusicEngine(this.state.meCurrentPage);
            return;
        }
        
        // If for some reason podcastSource is not set, default to MusicEngine
        await this.fetchEpisodesFromMusicEngine(1);
    };

    followToggle = async () => {
        if (!this.state.user) {
            this.setState({
                loadingText: '',
                errorMessage: 'Please signin to follow the podcast'
            });
            setTimeout(() => {
                this.setState({ errorMessage: '' });
            }, 5000);
            return;
        }
        try {
            this.loadingAnimation = setInterval(this.updateLoadingText, 500);
            const action = !this.state.following; // true to follow, false to unfollow
            let completed = false;

            const meToken = localStorage.getItem('me_access_token');
            if (meToken && this.state.podcastSource === 'musicengine') {
                try {
                    await musicEngineToggleFavorite({ id: this.state.podcast._id, objectType: 'podcast', action });
                    logEventPodcastFollowed(this.state.user, {
                        PodcastId: this.state.podcast._id,
                        PodcastName: this.state.podcast.title || '',
                        Author: this.state.podcast.author || '',
                        Category: this.state.podcast.categories || ''
                    });
                    this.setState((prev) => ({
                        following: !prev.following,
                        loadingText: '',
                        podcast: {
                            ...prev.podcast,
                            followers: Math.max(0, (prev.podcast?.followers || 0) + (action ? 1 : -1))
                        }
                    }));
                    completed = true;
                } catch (err) {
                    if (!(err && err.status === 401)) {
                        throw err;
                    }
                    // 401 from MusicEngine → fall back to legacy
                }
            }

            if (!completed) {
                // MusicEngine authentication required
                this.setState({ loadingText: '', errorMessage: 'Please sign in to follow this podcast.' });
                return;
            }
        } catch (error) {
            this.setState({
                loadingText: '',
                errorMessage: (typeof error === 'string' ? error : 'Follow failed')
            });
        } finally {
            if (this.loadingAnimation) {
                clearInterval(this.loadingAnimation);
            }
        }
    };

    handlePreviousClick = () => {
        if (this.state.nextLoading || this.state.previousLoading) {
            return;
        }
        if (this.state.podcastSource === 'musicengine') {
            if (this.state.meCurrentPage <= 1) {
                return;
            }
            const newPage = this.state.meCurrentPage - 1;
            this.setState(prev => {
                const mePages = { ...prev.mePages };
                // Show only previous page
                const allEpisodes = Array.isArray(mePages[newPage]) ? mePages[newPage] : [];
                const hasMore = (typeof prev.meLastPage === 'number' && newPage < prev.meLastPage) || (allEpisodes.length === prev.mePerPage);
                return {
                    previousLoading: true,
                    episodes: allEpisodes,
                    meCurrentPage: newPage,
                    meHasMore: !!hasMore
                };
            }, () => {
                this.setState({ previousLoading: false });
            });
            return;
        }
        // Legacy flow
        let nextSkip = this.state.nextSkip;
        if (!nextSkip || nextSkip <= 50) {
            return;
        }
        nextSkip = (nextSkip === 50) ? 0 : nextSkip - 100;
        this.setState({
            previousLoading: true,
            nextSkip: nextSkip
        }, async () => {
            this.loadingAnimation = setInterval(this.updateLoadingText, 500);
            await this.getEpisodes();
        });
    };

    handleNextClick = () => {
        if (this.state.nextLoading || this.state.previousLoading) {
            return;
        }
        if (this.state.podcastSource === 'musicengine') {
            if (!this.state.meHasMore) {
                return;
            }
            this.setState({ nextLoading: true }, async () => {
                this.loadingAnimation = setInterval(this.updateLoadingText, 500);
                const targetPage = this.state.meCurrentPage + 1;
                await this.fetchEpisodesFromMusicEngine(targetPage);
            });
            return;
        }
        // Legacy flow
        if (!this.state.nextPage) {
            return;
        }
        this.setState({
            nextLoading: true,
        }, async () => {
            this.loadingAnimation = setInterval(this.updateLoadingText, 500);
            await this.getEpisodes();
        });
    };

    handleIndex = (value) => {
        this.setState({ selectedIndex: value });
    };

    updateLoadingText = () => {
        this.setState((prevState) => {
            const newText = prevState.loadingText === '...' ? '.' : prevState.loadingText + '.';
            return { loadingText: newText };
        });
    };

    playEpisode = (episodeLike) => {
        if (!episodeLike) return;
        const episodeId = episodeLike._id || episodeLike.id || episodeLike.episodeId || episodeLike.episode_id;
        // Try to resolve the full episode object from state if possible
        const episode = this.state.episodes.find(e => (e._id || e.id) === episodeId) || episodeLike;
        if (!episodeId) {
            return;
        }
        this.setState({ selectedEpisode: episode }, () => {
            this.props.history.push({
                pathname: `/episodePage/${this.state.podcast._id}/${episodeId}`,
                state: { podcast: this.state.podcast, episode }
            });
        });
    };

    onKeyDown = (e) => {
        // Ignore when typing in form fields
        const target = e && e.target;
        const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
        const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
        if (isFormField) {
            return;
        }
        return;
        if (!e.key) {
            return;
        }
        if (e.key === 'ArrowRight') {
            this.handleNextClick();
        } else if (e.key === 'ArrowLeft') {
            this.handlePreviousClick();
        } else if (e.key === 'ArrowDown') {
            if (this.state.activeIndex > 53) {
                return;
            }
            this.setState({ activeIndex: this.state.activeIndex + 1 });
        } else if (e.key === 'ArrowUp') {
            if (this.state.activeIndex < -3) {
                return;
            }
            this.setState({ activeIndex: this.state.activeIndex - 1 });
        } else if (e.key === 'SoftLeft' || e.key === 'Enter') {
            if (this.state.activeIndex === -2) {
                this.followToggle();
            } else if (this.state.activeIndex === -1) {
                this.setState({ selectedIndex: (-1 * this.state.selectedIndex) - 1 });
            } else {
                const selectedEpisode = this.state.episodes[this.state.activeIndex];
                if (selectedEpisode) {
                    this.setState({ selectedEpisode: selectedEpisode }, () => {
                        this.playEpisode(this.state.selectedEpisode._id);
                    });
                }
            }
        } else if (e.key === 'SoftRight' || e.key === 'Backspace') {
            const target = e && e.target;
            const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
            const isFormField = tag === 'input' || tag === 'textarea' || tag === 'select' || (target && target.isContentEditable);
            if (!isFormField) {
            this.props.history.goBack();
            }
        }
        if (this.state.activeIndex !== -1) {
            this.setState({ selectedIndex: -1 });
        }
    };

    goBack = () => {
        const backTo = this.props.location && this.props.location.state && this.props.location.state.backTo;
        if (backTo && backTo.pathname) {
            this.props.history.push(backTo);
            return;
        }
        this.props.history.goBack();
    };

    render() {
        const {
            podcast,
            episodes,
            following,
            loadingText,
            selectedIndex,
            nextSkip,
            nextPage,
            meCurrentPage,
            meLastPage,
            activeIndex,
            podcastLoading,
            episodeLoading,
            nextLoading,
            previousLoading,
            previousButtonIndexNumber,
            nextButtonIndexNumber,
            successMessage,
            errorMessage,
        } = this.state;

        return (
            <div className={Style.container}>
                <Header title={podcast ? (podcast.title || 'Podcast') : 'Podcast'} back={true} backHandler={() => this.props.history.goBack()} />
                    {successMessage.length ? <span style={{ marginTop: 10, color: 'green', fontSize: '0.8rem' }}>{successMessage}</span> : <></>}
                    {errorMessage.length ? <span style={{ marginTop: 10, color: 'red', fontSize: '0.8rem' }}>{errorMessage}</span> : <></>}
                    {
                        podcastLoading ? (
                            <PageSpinner />
                        ) : (
                            <>
                                <div className={Style.podcastImage}>
                                    {podcast ? (
                                        <img src={podcast.thumbnailURL80x80 || defaultImage} alt='podcastImage' className={Style.podcastBanner} />
                                    ) : (
                                        <img src={spinGIF} alt='podcastImage' className={Style.imgSpinner} />
                                    )}
                                </div>
                                {podcast && (
                                    <div className={Style.podcastToolbar}>
                                        <div className={Style.metaDataBox}>
                                            <div className={Style.detailContainer}>
                                                <span className={Style.lightText}>{podcast.followers ? podcast.followers : '0'}</span>
                                                <span className={Style.lightText}>Followers</span>
                                            </div>
                                            <div className={Style.detailContainer}>
                                                <span className={Style.lightText}>
                                                {(() => {
                                                    // Always prefer known total when available (ME paginator total or mapped count)
                                                    const totalKnown = (this.state.podcastSource === 'musicengine')
                                                        ? (this.state.meTotalEpisodes || podcast.episodesCount)
                                                        : podcast.episodesCount;
                                                    return (totalKnown || episodes.length || 0);
                                                })()}
                                                </span>
                                                <span className={Style.lightText}>Episodes</span>
                                            </div>
                                        </div>
                                        <div
                                            id="followButton"
                                            className={Style.followBox}
                                            onClick={this.followToggle}
                                        >
                                            {following ? (
                                                <span
                                                    className={Style.lightText}
                                                    style={{ cursor: 'pointer', marginLeft: '3px', color: '#451e53', fontWeight: 'bold' }}
                                                >
                                                    Unfollow{loadingText}
                                                </span>
                                            ) : (
                                                <span className={Style.lightText} style={{ cursor: 'pointer', marginLeft: '3px' }}>
                                                    Follow{loadingText}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    }
                {!podcastLoading && podcast && (
                    <div id="divReadMoreLess" className={Style.description}>
                        <span className={Style.boldText}>{podcast.title ? podcast.title : ''}</span>
                        <span className={Style.lightText}>
                            <ReadMoreText
                                content={podcast.description ? podcast.description : ''}
                                length={25}
                                isFocus={activeIndex === -1}
                                index={0}
                                selectedIndex={selectedIndex}
                                handleIndex={this.handleIndex}
                                readLess={true}
                            />
                        </span>
                    </div>
                )}
                <div className={Style.episodeHeaderComponent}>
                    {
                        episodeLoading ? (
                            <PageSpinner />
                        ) : (
                            <>
                                {
                                    episodes.map((episode, index) => (
                                        <div
                                            className={`${Style.episodeList}`}
                                            id={`card-${index}`}
                                            key={index}
                                            tabIndex='0'
                                            onClick={() => this.playEpisode(episode)}
                                        >
                                            <div className={Style.episodeTile}>
                                                <div className={Style.episodeTitle}>
                                                    <span className={Style.lightText} style={{ marginLeft: '3px' }}>
                                                        {episode.title}{' '}
                                                    </span>
                                                </div>
                                                <div className={Style.playIcon}>
                                                    <img src={playbutton} style={{ height: '100%', width: '100%' }} alt='forward' />
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                }
                            </>
                        )
                    }
                    {
                        episodes.length > 0 && (
                        <div style={{ height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {/* Legacy prev */}
                                {this.state.podcastSource !== 'musicengine' && nextSkip > 50 && (
                                    <span className={Style.button} onClick={this.handlePreviousClick}>
                                        {previousLoading ? `Loading${loadingText}` : '< Previous'}
                                    </span>
                                )}
                                {/* ME prev */}
                                {this.state.podcastSource === 'musicengine' && meCurrentPage > 1 && (
                                <span className={Style.button} onClick={this.handlePreviousClick}>
                                    {previousLoading ? `Loading${loadingText}` : '< Previous'}
                                </span>
                            )}
                                {/* Spacer */}
                                <span style={{ width: '12px' }} />
                                {/* Legacy next */}
                                {this.state.podcastSource !== 'musicengine' && nextPage && (
                                    <span className={Style.button} onClick={this.handleNextClick} >
                                        {nextLoading ? `Loading${loadingText}` : 'Next >'}
                                    </span>
                                )}
                                {/* ME next */}
                                {this.state.podcastSource === 'musicengine' && this.state.meHasMore && (
                                <span className={Style.button} onClick={this.handleNextClick} >
                                    {nextLoading ? `Loading${loadingText}` : 'Next >'}
                                </span>
                            )}
                        </div>
                        )
                    }
                </div>
            </div>
        );
    }
}

export default PodcastDetail;